import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaHeart, FaTimes, FaVolumeUp, FaPause, FaSearch, FaFilter, FaTrash, FaArrowLeft, FaStar, FaBookOpen } from 'react-icons/fa';
import { API_BASE_URL } from '../config/api';
import { getAudioUrl } from '../data/vocabularyAudioMap';
import { playSmartAudio } from '../utils/audioUtils';

interface VocabularyWord {
  word: string;
  translation: string;
  persian?: string;
  english?: string;
  transliteration?: string;
  phonetics?: string;
  audioUrl?: string;
  category?: string;
  difficulty?: string;
  example?: string;
  pathwayId?: string;
  lessonId?: string;
  lessonTitle?: string;
  pathwayTitle?: string;
}

const FavoritesPage: React.FC = () => {
  const [allWords, setAllWords] = useState<VocabularyWord[]>([]);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const [audio, setAudio] = useState<HTMLAudioElement | null>(null);

  useEffect(() => {
    fetchAllWords();
    loadFavorites();
  }, []);

  const fetchAllWords = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/wordbanks`);
      const data = await response.json();
      
      if (data.success) {
        const transformedWords = data.data.map((word: any) => ({
          word: word.word || word.persian || '',
          translation: word.translation || word.english || '',
          persian: word.persian || word.word || '',
          english: word.english || word.translation || '',
          transliteration: word.transliteration || word.phonetics || '',
          phonetics: word.phonetics || word.transliteration || '',
          audioUrl: word.audioUrl || '',
          category: word.category || 'general',
          difficulty: word.difficulty || 'beginner',
          example: word.example || '',
          pathwayId: word.pathwayId,
          lessonId: word.lessonId,
          lessonTitle: word.lessonTitle,
          pathwayTitle: word.pathwayTitle
        }));
        
        setAllWords(transformedWords);
      }
    } catch (err) {
      console.error('Error fetching words:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = () => {
    const savedFavorites = localStorage.getItem('vocabulary-favorites');
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  };

  const saveFavorites = (newFavorites: string[]) => {
    setFavorites(newFavorites);
    localStorage.setItem('vocabulary-favorites', JSON.stringify(newFavorites));
  };

  const removeFavorite = (wordId: string) => {
    const newFavorites = favorites.filter(id => id !== wordId);
    saveFavorites(newFavorites);
  };

  const clearAllFavorites = () => {
    if (window.confirm('Are you sure you want to remove all favorites? This action cannot be undone.')) {
      saveFavorites([]);
    }
  };

  const playAudio = async (audioUrl: string, wordId: string) => {
    const word = allWords.find((w, index) => `${w.word}-${index}` === wordId);
    if (!word) return;

    // Try to get audio URL from mapping if not provided or if the provided URL fails
    let finalAudioUrl = audioUrl;
    if (!finalAudioUrl || finalAudioUrl === '') {
      finalAudioUrl = getAudioUrl(word.english, word.category || '') || '';
    }

    if (!finalAudioUrl) {
      // If no audio URL available, use text-to-speech as fallback
      try {
        setCurrentlyPlaying(wordId);
        await playSmartAudio(undefined, word.persian);
        setCurrentlyPlaying(null);
      } catch (error) {
        console.error('Error with text-to-speech:', error);
        setCurrentlyPlaying(null);
      }
      return;
    }

    try {
      // Stop current audio if playing
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }

      // Create new audio element
      const newAudio = new Audio(finalAudioUrl);
      setAudio(newAudio);
      setCurrentlyPlaying(wordId);

      // Add event listeners
      newAudio.addEventListener('ended', () => {
        setCurrentlyPlaying(null);
      });

      newAudio.addEventListener('error', (error) => {
        console.error('Audio playback error:', error);
        setCurrentlyPlaying(null);
        
        // Fallback to text-to-speech if audio file fails
        const word = allWords.find((w, index) => `${w.word}-${index}` === wordId);
        if (word) {
          console.log('🚫 No audio file available for:', word.persian);
          playSmartAudio(undefined, word.persian).catch(console.error);
        }
      });

      await newAudio.play();
    } catch (error) {
      console.error('Error playing audio:', error);
      setCurrentlyPlaying(null);
      
      // No audio file available
      const word = allWords.find((w, index) => `${w.word}-${index}` === wordId);
      if (word) {
        console.log('🚫 No audio file available for:', word.persian);
        try {
          await playSmartAudio(undefined, word.persian);
          console.log('✅ Audio fallback handled gracefully');
        } catch (speechError) {
          console.error('❌ Text-to-speech also failed:', speechError);
        }
      }
    }
  };

  // Get favorite words with their full data
  const favoriteWords = allWords.filter((word, index) => 
    favorites.includes(`${word.word}-${index}`)
  );

  // Filter favorites based on search and category
  const filteredFavorites = favoriteWords.filter(word => {
    const matchesSearch = searchTerm === '' || 
      word.persian.toLowerCase().includes(searchTerm.toLowerCase()) ||
      word.english.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (word.transliteration && word.transliteration.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || word.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Get unique categories from favorite words
  const categories = Array.from(new Set(favoriteWords.map(word => word.category))).sort();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your favorites...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-400 to-pink-500 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link 
                to="/vocabulary-garden"
                className="p-2 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-all"
              >
                <FaArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center space-x-3">
                <FaHeart className="w-8 h-8 text-white" />
                <div>
                  <h1 className="text-3xl font-bold">My Favorites</h1>
                  <p className="text-white text-opacity-90">
                    {favoriteWords.length} words saved • {filteredFavorites.length} showing
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {favorites.length > 0 && (
                <button
                  onClick={clearAllFavorites}
                  className="flex items-center space-x-2 px-4 py-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-all"
                >
                  <FaTrash className="w-4 h-4" />
                  <span className="font-medium">Clear All</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            {/* Search */}
            <div className="flex-1 w-full">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search your favorite words..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <FaFilter className="text-gray-500 w-4 h-4" />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="all">All Categories</option>
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Clear Search */}
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="p-3 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all"
                >
                  <FaTimes className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Results Summary */}
          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <span>
              Showing {filteredFavorites.length} of {favoriteWords.length} favorites
              {searchTerm && ` for "${searchTerm}"`}
              {selectedCategory !== 'all' && ` in ${selectedCategory}`}
            </span>
            <span className="text-red-600 font-medium">
              💖 {favoriteWords.length} total favorites
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        {favoriteWords.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">💔</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">No favorites yet</h2>
            <p className="text-gray-600 mb-6">
              Start exploring vocabulary and click the heart icon to save your favorite words!
            </p>
            <Link 
              to="/vocabulary-garden"
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Explore Vocabulary Garden
            </Link>
          </div>
        ) : filteredFavorites.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔍</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">No matches found</h2>
            <p className="text-gray-600 mb-6">
              Try adjusting your search terms or category filter.
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => setSearchTerm('')}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Clear Search
              </button>
              <button
                onClick={() => setSelectedCategory('all')}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Show All Categories
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredFavorites.map((word, index) => (
              <div 
                key={`favorite-${word.word}-${index}`}
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100 overflow-hidden"
              >
                {/* Word Card Header */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-800 mb-2 text-right" dir="rtl">
                        {word.persian}
                      </h3>
                      <p className="text-lg text-gray-600 mb-1">
                        {word.english}
                      </p>
                      {word.transliteration && (
                        <p className="text-sm text-gray-500 italic">
                          {word.transliteration}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end space-y-2 ml-4">
                      <button
                        onClick={() => removeFavorite(`${word.word}-${allWords.indexOf(word)}`)}
                        className="p-2 rounded-full bg-red-50 text-red-500 hover:bg-red-100 transition-all duration-200"
                        title="Remove from favorites"
                      >
                        <FaTimes className="w-4 h-4" />
                      </button>
                      {word.audioUrl && (
                        <button
                          onClick={() => playAudio(word.audioUrl!, `${word.word}-${index}`)}
                          className="p-2 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                          title="Play audio"
                        >
                          {currentlyPlaying === `${word.word}-${index}` ? (
                            <FaPause className="w-4 h-4" />
                          ) : (
                            <FaVolumeUp className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Category Badge */}
                  {word.category && (
                    <div className="mb-4">
                      <span className="inline-block px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                        {word.category.charAt(0).toUpperCase() + word.category.slice(1)}
                      </span>
                    </div>
                  )}

                  {/* Example Sentence */}
                  {word.example && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Example:</p>
                      <p className="text-sm text-gray-800 text-right" dir="rtl">
                        {word.example}
                      </p>
                    </div>
                  )}

                  {/* Lesson Info */}
                  {word.lessonTitle && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="flex items-center text-xs text-gray-500">
                        <FaBookOpen className="w-3 h-3 mr-1" />
                        <span className="truncate">{word.lessonTitle}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Practice Section */}
        {filteredFavorites.length > 0 && (
          <div className="mt-12 bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
              <FaStar className="w-5 h-5 text-yellow-500 mr-2" />
              Practice Your Favorite Words
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link 
                to="/flashcards"
                className="p-4 border-2 border-blue-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all text-center"
              >
                <div className="text-2xl mb-2">🃏</div>
                <h3 className="font-semibold text-gray-800">Flashcards</h3>
                <p className="text-sm text-gray-600">Review your favorites</p>
              </Link>
              <Link 
                to="/quiz"
                className="p-4 border-2 border-green-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition-all text-center"
              >
                <div className="text-2xl mb-2">🎯</div>
                <h3 className="font-semibold text-gray-800">Quiz</h3>
                <p className="text-sm text-gray-600">Test your knowledge</p>
              </Link>
              <Link 
                to="/ai-chat"
                className="p-4 border-2 border-purple-200 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-all text-center"
              >
                <div className="text-2xl mb-2">🤖</div>
                <h3 className="font-semibold text-gray-800">AI Practice</h3>
                <p className="text-sm text-gray-600">Practice with AI tutor</p>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage; 